import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarDays,
  ChevronRight,
  Clock,
  History,
  Users,
  Zap,
} from "lucide-react";
import { useCaseQuery } from "hooks/useCaseDashboard";
import {
  parseUtc,
  formatDuration,
  isCurrentSession,
} from "utils/caseDashboard";
import { Card, EmptyState, QueryState } from "./parts";

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

const HistoryView = ({ caseId, onOpen }) => {
  const { t } = useTranslation();
  const sessions = useCaseQuery(caseId, "sessions", { days: 7 }, 30000);
  const rows = sessions.data || [];

  const { days, totals, maxImpacts, maxDuration } = useMemo(() => {
    const now = Date.now();
    const list = rows
      .map((s) => {
        const start = parseUtc(s.started_at);
        const end = parseUtc(s.ended_at);
        const running = isCurrentSession(s, now);
        // A session that never ended and isn't current was abandoned.
        const abandoned = !end && !running;
        const durationMs = start
          ? end
            ? end - start
            : running
            ? now - start
            : null
          : null;
        return { ...s, start, end, running, abandoned, durationMs };
      })
      .filter((s) => s.start);
    const groups = new Map();
    for (const s of list) {
      const k = dayKey(s.start);
      if (!groups.has(k)) groups.set(k, { date: s.start, sessions: [] });
      groups.get(k).sessions.push(s);
    }
    return {
      days: [...groups.values()],
      totals: {
        sessions: list.length,
        impacts: list.reduce((n, s) => n + (Number(s.impacts) || 0), 0),
        hours: list.reduce((n, s) => n + (s.durationMs || 0), 0) / 3600e3,
      },
      maxImpacts: Math.max(1, ...list.map((s) => Number(s.impacts) || 0)),
      maxDuration: Math.max(1, ...list.map((s) => s.durationMs || 0)),
    };
  }, [rows]);

  const dayLabel = (d) => {
    const today = new Date();
    const yesterday = new Date(Date.now() - 864e5);
    if (dayKey(d) === dayKey(today)) return t("caseDashboard.history.today");
    if (dayKey(d) === dayKey(yesterday))
      return t("caseDashboard.history.yesterday");
    return d.toLocaleDateString([], {
      weekday: "long",
      day: "numeric",
      month: "short",
    });
  };
  const hhmm = (d) =>
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-[22px] font-bold text-[#111827]">
          {t("caseDashboard.tabs.history")}
        </h1>
        <p className="mt-0.5 text-[13px] text-[#6B7280]">
          {t("caseDashboard.history.subtitle")}
        </p>
      </div>
      <QueryState query={sessions} />

      {rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={History}
            title={
              sessions.loading
                ? t("caseDashboard.common.loading")
                : t("caseDashboard.history.empty")
            }
          />
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Total
              icon={CalendarDays}
              label={t("caseDashboard.history.totalSessions")}
              value={totals.sessions}
            />
            <Total
              icon={Clock}
              label={t("caseDashboard.history.totalTime")}
              value={formatDuration(totals.hours * 3600)}
            />
            <Total
              icon={Zap}
              label={t("caseDashboard.history.totalImpacts")}
              value={totals.impacts.toLocaleString()}
            />
          </div>

          {days.map((day) => (
            <section key={dayKey(day.date)}>
              <h2 className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
                {dayLabel(day.date)}
              </h2>
              <div className="overflow-hidden rounded-xl border border-[#F3F4F6] bg-white">
                {day.sessions.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => onOpen(s.id)}
                    className="group grid w-full grid-cols-[110px_1fr_auto_20px] items-center gap-x-5 border-b border-[#F3F4F6] px-4 py-3.5 text-left transition duration-150 last:border-b-0 hover:bg-[#FAFAFA] lg:grid-cols-[110px_minmax(0,1fr)_200px_200px_20px]"
                  >
                    <span>
                      <span className="block text-[15px] font-semibold tabular-nums text-[#111827]">
                        {hhmm(s.start)}
                      </span>
                      <span className="block text-[11px] text-[#9CA3AF]">
                        {s.end
                          ? t("caseDashboard.history.until", {
                              time: hhmm(s.end),
                            })
                          : ""}
                      </span>
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        {s.running && (
                          <span className="flex items-center gap-1 rounded-full bg-[#DCFCE7] px-2 py-0.5 text-[11px] font-semibold text-[#15803D]">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#16A34A]" />
                            {t("caseDashboard.history.running")}
                          </span>
                        )}
                        {s.abandoned && (
                          <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-[11px] font-medium text-[#6B7280]">
                            {t("caseDashboard.history.abandoned")}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-[13px] text-[#374151]">
                          <Users size={13} className="text-[#9CA3AF]" />
                          {t("caseDashboard.history.playerCount", {
                            count: Number(s.players) || 0,
                          })}
                        </span>
                      </span>
                      <span className="mt-0.5 block truncate font-mono text-[11px] text-[#C4C4C4]">
                        {s.id}
                      </span>
                    </span>
                    <Meter
                      value={
                        s.durationMs ? formatDuration(s.durationMs / 1000) : "—"
                      }
                      frac={(s.durationMs || 0) / maxDuration}
                      color="#93C5FD"
                      className="hidden lg:block"
                    />
                    <Meter
                      value={t("caseDashboard.history.impactCount", {
                        count: Number(s.impacts) || 0,
                      })}
                      frac={(Number(s.impacts) || 0) / maxImpacts}
                      color="#F4A26B"
                    />
                    <ChevronRight
                      size={16}
                      className="text-[#D1D5DB] transition group-hover:translate-x-0.5 group-hover:text-[#9CA3AF]"
                    />
                  </button>
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
};

const Total = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-[#F3F4F6] bg-white p-4">
    <div className="mb-2 flex items-center gap-1.5">
      <Icon size={14} className="text-[#6B7280]" />
      <span className="text-xs text-[#6B7280]">{label}</span>
    </div>
    <span className="text-[28px] font-bold tabular-nums leading-none text-[#111827]">
      {value}
    </span>
  </div>
);

const Meter = ({ value, frac, color, className = "" }) => (
  <span className={`min-w-0 ${className}`}>
    <span className="block text-right text-[13px] font-semibold tabular-nums text-[#111827]">
      {value}
    </span>
    <span className="mt-1 block h-1.5 rounded-full bg-[#F3F4F6]">
      <span
        className="block h-full rounded-full"
        style={{ width: `${Math.max(3, frac * 100)}%`, backgroundColor: color }}
      />
    </span>
  </span>
);

export default HistoryView;
